import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Your enquiry has been received. We will be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 lg:py-40 bg-gradient-to-b from-transparent via-[rgba(5,7,6,0.55)] to-[#050706]"
      data-testid="contact-section"
    >
      <div className="grid grid-cols-12 gap-y-16 lg:gap-x-12">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-5"
        >
          <h2
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream"
            data-testid="contact-heading"
          >
            Tell us about <br />
            your dream home.
          </h2>
          <div className="mt-16 space-y-6 text-sm font-light text-taupe">
            <div data-testid="contact-email">
              <p className="overline-label mb-2 !text-[10px]">Correspondence</p>
              <a href="mailto:studio@zeniquegroup.com" className="gold-underline-link text-cream hover:text-gold transition-colors duration-500">
                studio@zeniquegroup.com
              </a>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 lg:col-span-6 lg:col-start-7 flex flex-col gap-2"
          data-testid="contact-form"
        >
          <div className="grid md:grid-cols-2 gap-x-10">
            <input
              required
              value={form.name}
              onChange={update("name")}
              placeholder="Full name"
              className="lux-input"
              data-testid="contact-name-input"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="Email address"
              className="lux-input"
              data-testid="contact-email-input"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-x-10">
            <input
              value={form.phone}
              onChange={update("phone")}
              placeholder="Telephone (optional)"
              className="lux-input"
              data-testid="contact-phone-input"
            />
            <select
              value={form.service}
              onChange={update("service")}
              className="lux-input appearance-none cursor-pointer"
              data-testid="contact-service-select"
            >
              <option value="" className="bg-noir">Service of interest</option>
              <option value="Interior Styling & Direction" className="bg-noir">Interior Styling & Direction</option>
              <option value="Spatial Curation & Layout" className="bg-noir">Spatial Curation & Layout</option>
              <option value="Bespoke Furniture & Sourcing" className="bg-noir">Bespoke Furniture & Sourcing</option>
              <option value="Art & Object Consultancy" className="bg-noir">Art & Object Consultancy</option>
              <option value="Turnkey Residences" className="bg-noir">Turnkey Residences</option>
            </select>
          </div>
          <textarea
            required
            rows={5}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us about your space, your city, and the feeling you are after."
            className="lux-input resize-none"
            data-testid="contact-message-input"
          />
          <button
            type="submit"
            disabled={submitting}
            className="group mt-10 self-start inline-flex items-center gap-4 border border-gold/40 px-10 py-4 text-xs tracking-[0.3em] uppercase text-gold hover:bg-gold hover:text-noir transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            {submitting ? "Sending…" : "Send Enquiry"}
            <span className="h-px w-8 bg-gold group-hover:bg-noir transition-colors duration-500" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
